import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Fade,
  Zoom,
  Chip,
  Stack,
} from '@mui/material';
import {
  Build,
  Settings,
} from '@mui/icons-material';
import LoadingImage from '../components/LoadingImage';

const Home: React.FC = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  const images = [
    '/1.jpg',
    '/2.png', 
    '/3.jpg',
    '/4.png',
    '/5.jpg'
  ];

  const brandImages = [
    '/10.jpeg',
    '/11.jpeg',
    '/12.png',
    '/13.jpg',
    '/14.png',
    '/15.png',
    '/16.jpg',
    '/17.png'
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [images.length]);

  // SEO Meta Tags
  useEffect(() => {
    document.title = "Kale Treyler Frigo - Mersin'in En Güvenilir Frigo Dorse Tamir Servisi";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Mersin'de profesyonel frigo dorse tamiri, araç içi yalıtım, double deck sistem ve yedek parça hizmetleri. Kale Treyler Frigo ile araçlarınızı yeniden hayata döndürün.");
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', "frigo dorse tamiri Mersin, araç içi yalıtım, double deck sistem, yedek parça, frigo servis, Kale Treyler, MIG Mersin Tır Parkı");
    }
    
    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Kale Treyler Frigo - Mersin'in En Güvenilir Frigo Dorse Tamir Servisi");
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Mersin'de profesyonel frigo dorse tamiri, araç içi yalıtım, double deck sistem ve yedek parça hizmetleri. Kale Treyler Frigo ile araçlarınızı yeniden hayata döndürün.");
    }

    // Update Canonical URL
    const canonicalLink = (document.querySelector('link[rel="canonical"]') || document.createElement('link')) as HTMLLinkElement;
    canonicalLink.setAttribute('rel', 'canonical');
    canonicalLink.setAttribute('href', 'https://kaletreyler.com');
    if (!document.head.contains(canonicalLink)) {
      document.head.appendChild(canonicalLink);
    }
  }, []);

  const features = [
    {
      icon: <Build sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Profesyonel Tamir',
      description: 'Frigo dorse kasalarınızda uzman ekibimizle profesyonel tamir hizmeti',
      color: '#3b82f6'
    },
    {
      icon: <Settings sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Bakım',
      description: 'Periyodik bakım ve önleyici tamir hizmetleri',
      color: '#8b5cf6'
    }
  ];

  const stats = [
    { number: '100%', label: 'Müşteri Memnuniyeti' },
    { number: '24/7', label: 'Servis' },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #ffffff 0%, #f1f5f9 50%, #e2e8f0 100%)',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 80%, rgba(30, 58, 138, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(30, 58, 138, 0.1) 0%, transparent 50%)',
            zIndex: 0,
          }
        }}
      >
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, px: { xs: 2, md: 4, lg: 6 } }}>
          <Grid container spacing={{ xs: 4, md: 6, lg: 8 }} alignItems="center">
            {/* Left Content */}
            <Grid item xs={12} lg={6}>
              <Fade in timeout={1000}>
                <Box>
                  <Chip 
                    label="Mersin'in Lider Frigo Dorse Tamir Servisi" 
                    sx={{ 
                      mb: 3, 
                      bgcolor: 'primary.main', 
                      color: 'white',
                      fontSize: '0.9rem',
                      fontWeight: 'bold',
                      px: 2,
                      py: 1
                    }} 
                  />
                  <Typography
                    variant="h1"
                    component="h1"
                    gutterBottom
                    sx={{
                      fontWeight: 800,
                      color: 'text.primary',
                      mb: 3,
                      lineHeight: 1.1,
                      fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                    }}
                  >
                    Güvenilir Frigo
                    <Box 
                      component="span" 
                      sx={{ 
                        color: 'primary.main', 
                        display: 'block',
                        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      Dorse Tamir Çözümleri
                    </Box>
                  </Typography>
                  <Typography
                    variant="h5"
                    paragraph
                    sx={{
                      mb: 4,
                      color: 'text.secondary',
                      lineHeight: 1.6,
                      maxWidth: '500px',
                    }}
                  >
                    2018'dan beri frigo dorse kasalarında uzman ekibimizle güvenilir ve kaliteli tamir hizmetleri sunuyoruz.
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 4 }}>
                    <Button
                      component={RouterLink}
                      to="/hizmetlerimiz"
                      variant="contained"
                      size="large"
                      sx={{
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        borderRadius: 2,
                        bgcolor: 'primary.main',
                        '&:hover': {
                          bgcolor: 'primary.dark',
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Hizmetlerimiz
                    </Button>
                    <Button
                      component={RouterLink}
                      to="/iletisim"
                      variant="outlined"
                      size="large"
                      sx={{
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        borderRadius: 2,
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        '&:hover': {
                          borderColor: 'primary.dark',
                          bgcolor: 'primary.50',
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      İletişim
                    </Button>
                  </Stack>
                  
                  {/* Stats */}
                  <Grid container spacing={3}>
                    {stats.map((stat, index) => (
                      <Grid item xs={6} sm={3} key={index}>
                        <Box textAlign="center">
                          <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            {stat.number}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium' }}>
                            {stat.label}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Fade>
            </Grid>

            {/* Right Content - Image */}
            <Grid item xs={12} lg={6}>
              <Fade in timeout={1500}>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: '600px',
              aspectRatio: '1 / 1',
                    borderRadius: 4,
              overflow: 'hidden',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
              mx: 'auto',
            }}
          >
            {images.map((image, index) => (
              <Box
                key={index}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  opacity: index === currentImageIndex ? 1 : 0,
                  transition: 'opacity 1s ease-in-out',
                        zIndex: index === currentImageIndex ? 2 : 1,
                      }}
                    >
                      <LoadingImage
                        src={image}
                        alt={`Frigo Dorse Tamir ${index + 1}`}
                        style={{
                width: '100%',
                height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                </Box>
                  ))}

            {/* Image Indicators */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 20,
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: 1,
                      zIndex: 3,
              }}
            >
              {images.map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: index === currentImageIndex ? 'white' : 'rgba(255,255,255,0.5)',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    '&:hover': {
                      bgcolor: 'white',
                    },
                  }}
                  onClick={() => setCurrentImageIndex(index)}
                />
              ))}
            </Box>
          </Box>
              </Fade>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Brands Section */}
      <Box sx={{ bgcolor: 'white', py: 8 }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, md: 4, lg: 6 } }}>
          <Fade in timeout={1000}>
            <Box textAlign="center" mb={6}>
              <Typography variant="h2" component="h2" gutterBottom color="text.primary" sx={{ fontWeight: 'bold' }}>
               
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
                Markaların özel servisi olarak hizmet veriyoruz
              </Typography>
            </Box>
          </Fade>
          
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              position: 'relative',
              height: 120,
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: 100,
                background: 'linear-gradient(to right, white, transparent)',
                zIndex: 2,
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                width: 100,
                background: 'linear-gradient(to left, white, transparent)',
                zIndex: 2,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                animation: 'slide 20s linear infinite',
                '@keyframes slide': {
                  '0%': { transform: 'translateX(0)' },
                  '100%': { transform: 'translateX(-50%)' },
                },
              }}
            >
              {/* İlk set */}
              {brandImages.map((brand, index) => (
                <Box
                  key={`first-${index}`}
                  sx={{
                    flex: '0 0 200px',
                    height: 80,
                    mx: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'white',
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  <LoadingImage
                    src={brand}
                    alt={`Marka ${index + 1}`}
                    style={{
                      maxWidth: '80%',
                      maxHeight: '80%',
                      objectFit: 'contain',
                      transform: ['13.jpg', '14.png', '16.jpg', '17.png'].includes(brand.split('/').pop() || '') 
                        ? 'translateY(-30px)' 
                        : 'none',
                    }}
                  />
                </Box>
              ))}
              {/* İkinci set (sonsuz döngü için) */}
              {brandImages.map((brand, index) => (
                <Box
                  key={`second-${index}`}
                  sx={{
                    flex: '0 0 200px',
                    height: 80,
                    mx: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'white',
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  <LoadingImage
                    src={brand}
                    alt={`Marka ${index + 1}`}
                    style={{
                      maxWidth: '80%',
                      maxHeight: '80%',
                      objectFit: 'contain',
                      transform: ['13.jpg', '14.png', '16.jpg', '17.png'].includes(brand.split('/').pop() || '') 
                        ? 'translateY(-30px)' 
                        : 'none',
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ bgcolor: 'grey.50', py: 12 }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, md: 4, lg: 6 } }}>
          <Fade in timeout={1000}>
            <Box textAlign="center" mb={8}>
              <Typography variant="h2" component="h2" gutterBottom color="text.primary" sx={{ fontWeight: 'bold' }}>
            Neden Bizi Seçmelisiniz?
          </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto' }}>
                Frigo dorse tamirinde uzman ekibimizle size en kaliteli hizmeti sunuyoruz
          </Typography>
        </Box>
          </Fade>

          <Grid container spacing={{ xs: 4, md: 6, lg: 8 }} alignItems="center">
            {/* Features Cards */}
            <Grid item xs={12} lg={6}>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Zoom in timeout={1000 + index * 200}>
              <Card
                        elevation={0}
                sx={{
                  height: '100%',
                  textAlign: 'center',
                          p: 4,
                          border: '1px solid',
                          borderColor: 'grey.200',
                          transition: 'all 0.3s ease',
                  '&:hover': {
                            transform: 'translateY(-8px)',
                            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                            borderColor: feature.color,
                  },
                }}
              >
                <CardContent>
                          <Box 
                            mb={3}
                            sx={{
                              display: 'inline-flex',
                              p: 2,
                              borderRadius: '50%',
                              bgcolor: `${feature.color}15`,
                              border: `2px solid ${feature.color}30`
                            }}
                          >
                            {feature.icon}
                          </Box>
                          <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {feature.title}
                  </Typography>
                          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
                    </Zoom>
            </Grid>
          ))}
        </Grid>
            </Grid>

            {/* İşletme Görseli */}
            <Grid item xs={12} lg={6}>
              <Fade in timeout={1500}>
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      transition: 'transform 0.3s ease',
                    }
                  }}
                >
                  <LoadingImage
                    src="/18.jpeg"
                    alt="Kale Treyler İşletme Görseli"
                    style={{
                      width: '100%',
                      height: '400px',
                      objectFit: 'cover',
                    }}
                  />
                  <Box
        sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                      p: 3,
          color: 'white',
        }}
      >
                    <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Kale Treyler
          </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9 }}>
                     
          </Typography>
                  </Box>
                </Box>
              </Fade>
            </Grid>
        </Grid>
        </Container>
      </Box>

    </Box>
  );
};

export default Home; 